/**
 * Money. Integer paise in, string out.
 *
 * WHY THIS FILE EXISTS: web and mobile each had their own, and they disagreed.
 * Web's formatter set `maximumFractionDigits: 0` and discarded paise; mobile's
 * `splitPaise` kept them. Two screens formatted money with a bare
 * `(paise/100).toFixed(2)`, which produces `₹1234.50` — no Indian grouping at
 * all. Same number, three answers.
 *
 * MASKING IS NOT HERE. Web's `formatINR` hides values behind a privacy toggle by
 * consulting a browser-only store. Core cannot import that and must not know
 * about it, so these are the RAW formatters and each client wraps them.
 *
 * WEB-ONLY ON THE CLIENT SIDE: `formatAmountRaw` and `formatCompactRaw` below
 * are built on `Intl.NumberFormat("en-IN", ...)`. That is fine for web, which
 * runs in a real browser with full ICU data. It is NOT fine for React
 * Native/Hermes: Hermes ships a trimmed ICU and `en-IN` digit grouping has
 * differed between Android versions, so comma placement would depend on the
 * handset. Mobile must NOT call these two formatters — use mobile's own
 * `formatPaise`/`formatRupees` in `frontend/mobile/src/components/ui/money.ts`, which
 * implements Indian grouping by hand for exactly this reason. Note
 * `formatCompactRaw` falls through to `formatAmountRaw` for sub-thousand
 * values, so it inherits the same `Intl` dependency and is equally web-only;
 * only `splitPaise` (pure integer arithmetic, no `Intl`) is safe to share.
 */

/** Indian digit grouping: 10,00,000 — not 1,000,000. */
const INR_WHOLE = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const INR_PAISE = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export interface FormatAmountOptions {
  /**
   * Always print two decimals, even for a whole amount (`₹2,500.00`). For
   * columns that must align on the decimal point. Default false: the EXACT
   * value with `.00` dropped only when there are no paise.
   */
  paise?: boolean;
}

/** Integer paise, rounded half away from zero (computed shares can be fractional). */
function toWholePaise(paise: number): number {
  return paise < 0 ? -Math.round(-paise) : Math.round(paise);
}

/**
 * Paise -> `₹2,500.45`. EXACT — never rounds paise away (owner, 2026-09-28:
 * "₹2500.45 like this no round off"). A whole amount prints `₹2,500`, not
 * `₹2,500.00`, unless `opts.paise` asks for aligned decimals. Indian grouping:
 * `₹12,34,567.89`. Negative: `-₹2,500.45`.
 */
export function formatAmountRaw(paise: number, opts: FormatAmountOptions = {}): string {
  const p = toWholePaise(paise);
  if (opts.paise || p % 100 !== 0) return INR_PAISE.format(p / 100);
  return INR_WHOLE.format(p / 100);
}

/** Same as `formatAmountRaw` but without the `₹` — `2,500.45` / `2,500`. */
export function formatAmountPlainRaw(paise: number, opts: FormatAmountOptions = {}): string {
  const p = toWholePaise(paise);
  const frac = opts.paise || p % 100 !== 0 ? 2 : 0;
  return (p / 100).toLocaleString("en-IN", {
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });
}

/** Rupees (possibly fractional, e.g. a chart series value) -> exact `₹2,500.45`. */
export function formatRupeesRaw(rupees: number, opts: FormatAmountOptions = {}): string {
  return formatAmountRaw(rupees * 100, opts);
}

export interface SplitAmount {
  sign: -1 | 0 | 1;
  rupees: string;
  paise: string;
}

/**
 * Split integer paise into sign, rupees and paise.
 *
 * Non-integer input is rounded HALF AWAY FROM ZERO. `Math.round` alone rounds
 * -0.5 towards zero, so a negative computed share would drift; N co-owner shares
 * of a payment must still sum to the payment exactly.
 */
export function splitPaise(totalPaise: number): SplitAmount {
  const rounded = totalPaise < 0 ? -Math.round(-totalPaise) : Math.round(totalPaise);
  const sign: -1 | 0 | 1 = rounded > 0 ? 1 : rounded < 0 ? -1 : 0;
  const abs = Math.abs(rounded);
  return {
    sign,
    rupees: String(Math.floor(abs / 100)),
    paise: String(abs % 100).padStart(2, "0"),
  };
}

// In RUPEES, not paise — this reproduces web's original formatCompactINR
// exactly, which rounds to whole rupees FIRST and tiers on THAT rounded value.
// 1 thousand / 1 lakh (1e5) / 1 crore (1e7) rupees.
const K_RUPEES = 1_000;
const LAKH_RUPEES = 100_000;
const CRORE_RUPEES = 10_000_000;

/**
 * `₹4.5k` / `₹2.25L` / `₹1.5Cr`. Lakh and crore, never million.
 *
 * THIS IS WEB'S RULE, NOT A SHARED ONE. It is a byte-for-byte port of the
 * ORIGINAL `frontend/web/src/lib/utils.ts::formatCompactINR` number logic: round to
 * whole rupees first, tier at rupees 1e3 / 1e5 / 1e7, two decimal places at
 * L/Cr (one at k), trailing zeros stripped. An earlier draft of this function
 * replaced that with a coarser, lakh/crore-only, one-decimal formatter with no
 * k-tier — which silently changed web's on-screen output for every amount
 * under a lakh and every non-round amount above it. This version fixes that
 * regression by reproducing web exactly, quirks included (see below).
 *
 * Mobile's `compactRupees` / `formatPaise({ compact: true })` in
 * `frontend/mobile/src/components/ui/money.ts` is a DIFFERENT, deliberately different
 * formatter — uppercase `'K'`, a space before the unit (`"1.23 Cr"` vs this
 * function's `"₹1.23Cr"`), and its own rounding and thresholds. It was never
 * the same function as this one and must not be wired to it: doing so would
 * silently restyle every compact figure on one platform.
 *
 * KNOWN QUIRK, PRESERVED ON PURPOSE: at the k/L/Cr tiers the original divides
 * the SIGNED rupee value (not its absolute value) and then prepends a sign
 * computed separately, so a negative amount renders with the sign twice —
 * `formatCompactRaw(-100000)` is `"-₹-1k"`, not `"-₹1k"`. That is what
 * production has always done; this port keeps it byte-identical rather than
 * "fixing" behaviour nobody asked this task to change.
 */
export function formatCompactRaw(paise: number): string {
  const rupees = Math.round(paise / 100);
  const abs = Math.abs(rupees);
  const sign = rupees < 0 ? "-" : "";
  if (abs >= CRORE_RUPEES) {
    return `${sign}₹${(rupees / CRORE_RUPEES).toFixed(2).replace(/\.?0+$/, "")}Cr`;
  }
  if (abs >= LAKH_RUPEES) {
    return `${sign}₹${(rupees / LAKH_RUPEES).toFixed(2).replace(/\.?0+$/, "")}L`;
  }
  if (abs >= K_RUPEES) {
    return `${sign}₹${(rupees / K_RUPEES).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return formatAmountRaw(paise);
}
